import React, { useState } from 'react';
import { api } from '../context/AuthContext';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Lock, Eye, EyeOff, Loader2, KeyRound, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const onlyDigits = (v) => v.replace(/\D/g, '').slice(0, 6);

const ForceChangePasswordPage = () => {
  const { updateUser } = useAuth();
  const navigate = useNavigate();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!currentPassword || !newPassword || !confirmPassword) {
      setError('Semua field harus diisi');
      return;
    }

    if (!/^\d{6}$/.test(newPassword)) {
      setError('Password baru harus 6 digit angka');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Password baru tidak cocok');
      return;
    }

    if (currentPassword === newPassword) {
      setError('Password baru tidak boleh sama dengan password lama');
      return;
    }

    setSubmitting(true);

    try {
      await api.post('/auth/force-change-password', {
        current_password: currentPassword,
        new_password: newPassword,
      });

      toast.success('Password berhasil diubah!');
      updateUser({ must_change_password: false });
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Gagal mengubah password');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-100 to-slate-200 p-4">
      <Card className="w-full max-w-sm">
        <CardHeader className="text-center pb-2">
          <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
            <KeyRound className="w-8 h-8 text-primary" />
          </div>
          <CardTitle className="text-xl">Ganti Password</CardTitle>
          <CardDescription>Buat password baru yang mudah Anda ingat</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="change-password-form">
            {error && (
              <Alert variant="destructive" data-testid="change-password-error">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <Label htmlFor="current">Password Saat Ini</Label>
              <div className="relative">
                <Input
                  id="current"
                  type={showCurrentPassword ? 'text' : 'password'}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="Password yang diberikan HR / WhatsApp"
                  className="pr-10"
                  data-testid="current-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                  data-testid="toggle-current-password-visibility"
                >
                  {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="new">Password Baru (6 Digit Angka)</Label>
              <Input
                id="new"
                type="text"
                inputMode="numeric"
                value={newPassword}
                onChange={(e) => setNewPassword(onlyDigits(e.target.value))}
                placeholder="●●●●●●"
                className="text-center text-2xl tracking-[0.5em] font-semibold"
                maxLength={6}
                data-testid="new-password-input"
              />
              <p className="text-xs text-muted-foreground text-center">Hanya angka 0-9, tepat 6 digit</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirm">Ulangi Password Baru</Label>
              <Input
                id="confirm"
                type="text"
                inputMode="numeric"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(onlyDigits(e.target.value))}
                placeholder="●●●●●●"
                className="text-center text-2xl tracking-[0.5em] font-semibold"
                maxLength={6}
                data-testid="confirm-password-input"
              />
              {newPassword.length === 6 && confirmPassword.length === 6 && (
                <div className={`flex items-center justify-center gap-1 text-xs ${newPassword === confirmPassword ? 'text-green-600' : 'text-red-500'}`}>
                  {newPassword === confirmPassword ? (
                    <><CheckCircle className="w-3 h-3" /> Password cocok</>
                  ) : (
                    'Password tidak cocok'
                  )}
                </div>
              )}
            </div>

            <Button type="submit" className="w-full" disabled={submitting} data-testid="submit-change-password-btn">
              {submitting ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Menyimpan...</>
              ) : (
                <><Lock className="w-4 h-4 mr-2" />Ubah Password</>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default ForceChangePasswordPage;
